import React, { FC, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Table, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { P, S, M, psm, Package, CompoundDocument } from "./types/Types";
import Documents from "./Documents";
import styles from "./PackagesEditor.module.scss";

interface IPackagesEditorProps {
    setShow: (show: boolean) => void;
    packages: Package[];
    setPackages: (packages: Package[]) => void;
    savePackages: (packages: Package[]) => void;
    pkgEditorAction: "CreateNew" | "AddDoc" | "Rename"
    pkgSelect?: Package;
    setPkgSelect?: (packageSelect: Package) => void;
    setIsPackagesDirty: (isPackagesDirty: boolean) => void;
}

export const PackagesEditor: FC<IPackagesEditorProps> = (props) => {
    const [pkgPSM, setPkgPSM] = useState<string>(props.pkgSelect?.tags?.some((tag) => psm.includes(tag))? props.pkgSelect.tags.find((tag) => psm.includes(tag)).name : "");
    const [newPkg, setNewPkg] = useState<Package>(props.pkgSelect??
        {
            name: "",
            tags: [],
            doc_list: [],
        }
    );

    const [showPkgDocPicker, setShowPkgDocPicker] = useState<boolean>(props.pkgEditorAction === "AddDoc");
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");

    const handleCancel = () => {
        props.setPkgSelect(null);
        setShowPkgDocPicker(false);
        props.setShow(false);
    }

    const handleNext = () => {
        const trimmedName = newPkg.name.trim();
        const LEGAL_CHAR_REGEX = /"/;
        setNewPkg({...newPkg, name: trimmedName});
        if (trimmedName === "" || (pkgPSM != "Purchase" && pkgPSM != "Sale" && pkgPSM != "Mortgage")) {
            setShowAlert(true);
            setAlertMessage("Please enter a package name and file type.");
        } else if (props.packages.flatMap((pkgs) => pkgs.children).some((pkg) => pkg.name === trimmedName)) {
            setShowAlert(true);
            setAlertMessage("A package with the same name already exists in this file type. Please choose a different package name.");
        } else if (LEGAL_CHAR_REGEX.test(trimmedName)) {
            setShowAlert(true);
            setAlertMessage(' " is not allowed in package name.');
        } else {
            let psm;
            switch (pkgPSM) {
                case "Purchase":
                    psm = P;
                    break;
                case "Sale":
                    psm = S;
                    break;
                case "Mortgage":
                    psm = M;
                    break;
            }
            setNewPkg({
                ...newPkg,
                tags: [psm]
            });
            setShowPkgDocPicker(true);
        }
    }

    const handlePkgCreateNew = async () => {
        const newPackages = props.packages.map((pkgs) => {
            if (pkgs.tags.some((tag) => tag.name === pkgPSM)) {
                return {
                    ...pkgs,
                    children: [...pkgs.children, newPkg]
                }
            } else {
                return pkgs;
            }
        });
        props.setPackages(newPackages);
        props.savePackages(newPackages);
        
        handleCancel();
    }

    const handlePkgEdit = () => {
        const LEGAL_CHAR_REGEX = /"/;
        if (props.pkgEditorAction === "Rename") {
            if (newPkg.name === "") {
                setShowAlert(true);
                setAlertMessage("Package name cannot be empty.");
                return;
            } else if (LEGAL_CHAR_REGEX.test(newPkg.name)) {
                setShowAlert(true);
                setAlertMessage(' " is not allowed in package name.');
                return;
            } else if (props.packages.flatMap((pkgs) => pkgs.children).some((pkg) => pkg.name === newPkg.name)) {
                setShowAlert(true);
                setAlertMessage("A package with the same name already exists in this file type. Please choose a different package name.");
                return;
            }   
        }

        const newPackages = props.packages.map((pkgs) => {
            if (pkgs.tags.some((tag) => tag.name === pkgPSM)) {
                return {
                    ...pkgs,
                    children: pkgs.children.map((pkg) => {
                        if (pkg.name === props.pkgSelect?.name) {
                            return newPkg;  // overwrite the existing package
                        } else {
                            return pkg;
                        }
                    })
                }
            } else {
                return pkgs;
            }
        });
        props.setPackages(newPackages);
        props.savePackages(newPackages);

        handleCancel();
    }

    return (
        <>
            <Modal 
                show={true} 
                centered
                size={props.pkgEditorAction === "CreateNew" ? ( "lg" ) : ( "sm" )}
            >
                <Modal.Header>
                    <Modal.Title>
                        {props.pkgEditorAction === "CreateNew" ? ( "Create New Package" ) : ( "Rename Package")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showAlert && (
                        <Alert variant="danger" onClose={() => setShowAlert(false)} dismissible>{alertMessage}</Alert>
                    )}

                    <Row>
                        <Form.Group as={Col} xs={props.pkgEditorAction === "CreateNew" ? ( 8 ) : ( 12 )}>
                            <Form.Label>Package Name</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={newPkg.name} 
                                onChange={(e) => setNewPkg({...newPkg, name: e.target.value.toString()})}
                                placeholder="enter package name.."
                            />
                        </Form.Group>

                        {props.pkgEditorAction === "CreateNew" && (
                            <Form.Group as={Col} xs={4}>
                                <Form.Label>File Type</Form.Label>
                                <Form.Select 
                                    value={pkgPSM}
                                    onChange={(e) => setPkgPSM(e.target.value)}
                                    title="Select File Type"
                                    aria-label="File Type"
                                >
                                    <option value={""}>Select</option>
                                    <option value={"Purchase"}>Purchase</option>
                                    <option value={"Sale"}>Sale</option>
                                    <option value={"Mortgage"}>Mortgage</option>
                                </Form.Select >
                            </Form.Group>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="text" onClick={() => handleCancel()}>Cancel</Button>
                    {props.pkgEditorAction === "Rename" ? (
                        <Button variant="primary" onClick={() => handlePkgEdit()}>Save</Button>
                    ) : (
                        <Button variant="primary" onClick={() => handleNext()}>Next</Button>
                    )}
                </Modal.Footer>
            </Modal>

            {showPkgDocPicker && (
                <Modal 
                    show={showPkgDocPicker} 
                    size="xl"
                    centered
                >
                    <Modal.Header>
                        <h2>Edit Package</h2>
                    </Modal.Header>

                    <Modal.Body>
                        <Row className="gx-0" style={{height: '100%'}}>
                            <Col xs={7} style={{height: '100%', overflow: 'auto'}} className={styles.addToPackage}>
                                <Documents 
                                    screenType="PackageEditor"
                                    newPkg={newPkg}
                                    setNewPkg={setNewPkg}
                                />
                            </Col>

                            <Col style={{height: '100%', overflow: 'auto'}}>
                                <Card className={styles.currentPackage + " card-list"} style={{overflow: 'auto', marginBottom: '0'}}>
                                    <Card.Header>
                                        <h2 className="text-truncate">Package: {`${newPkg.name}`}</h2>
                                    </Card.Header>

                                    <Card.Body>
                                        <Table hover borderless>
                                            <thead>
                                                <tr>
                                                    <th>Documents in Package</th>
                                                    <th style={{width:'40px'}}></th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {newPkg.doc_list.map((doc, index) => (
                                                    <tr key={index} onClick={() => {
                                                        setNewPkg({
                                                            ...newPkg,
                                                            doc_list: newPkg.doc_list.filter((_, i) => i !== index)
                                                        });
                                                    }}>
                                                            <OverlayTrigger placement="top" delay={300} overlay={
                                                                <Tooltip>{doc.name}</Tooltip>
                                                            }>
                                                        <td className="text-truncate">
                                                            {doc.template_type_id === 10 ? (
                                                                    <i className="fa-solid fa-file-pdf status-pdf" style={{marginRight: '5px'}}></i>
                                                                ) : doc.tags?.includes(CompoundDocument) ? (
                                                                    <i className="fa-solid fa-memo status-compound" style={{marginRight: '5px'}}></i>
                                                                ) : (
                                                                    <i className="fa-solid fa-file-word status-word" style={{marginRight: '5px'}}></i>
                                                                )}
                                                                {doc.name}
                                                        </td>
                                                            </OverlayTrigger>
                                                        <td width="20px">
                                                            <i className="fa-regular fa-trash-can"/>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-between">
                        <Button variant="outline-primary" onClick={() => handleCancel()}>Cancel</Button>
                        {props.pkgEditorAction === "AddDoc" ? (
                            <Button variant="primary" onClick={() => handlePkgEdit()}>Save</Button>
                        ) : (
                            <Button variant="primary" onClick={() => handlePkgCreateNew()}>Create Package</Button>
                        )}
                    </Modal.Footer>
                </Modal>
            )}
        </>
    )
}